import { useState, useRef, useEffect } from 'react';
import { Plus, Trash2, CheckCircle2, XCircle, ChevronDown } from 'lucide-react';

const SPACE_TYPES = [
  "Office",
  "Retail / Store Area",
  "Tenant Storage",
  "Exclusive Exterior (Balcony/Terrace)",
  "Public Corridor",
  "Restroom / Washroom",
  "Janitorial Closet",
  "Electrical / Telephone / Data Room",
  "Mechanical Room (Floor-level)",
  "Main Building Lobby",
  "Loading Dock / Truck Court",
  "Concierge / Security Desk",
  "Fire Control Room",
  "Central Mechanical Plant",
  "Shared Conference Room",
  "Fitness Center / Gym",
  "Daycare Facility",
  "Food Service / Cafeteria",
  "Stairs",
  "Elevator Shaft",
  "Atria (above finished floor)",
  "Pipe / Duct Shaft",
  "Parking / Surface Lot",
];

const NON_BUILDING_SF_TYPES = new Set([
  "Parking / Surface Lot",
  "Exclusive Exterior (Balcony/Terrace)",
]);

function EditableCell({ value, field, spaceId, onSave, type = 'text' }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? '');
  const inputRef = useRef(null);

  useEffect(() => { setVal(value ?? ''); }, [value]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const save = () => {
    setEditing(false);
    const parsed = type === 'number' ? (parseFloat(val) || null) : val;
    if (parsed !== value) onSave(spaceId, { [field]: parsed });
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={val}
        type={type}
        onChange={(e) => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
        data-testid={`edit-cell-${field}-${spaceId}`}
        className="bg-card border border-primary/50 rounded-sm px-1.5 py-0.5 font-mono text-xs text-foreground outline-none w-full min-w-0"
      />
    );
  }

  const display = value != null && value !== '' ? String(value) : '--';
  return (
    <span
      onClick={() => setEditing(true)}
      data-testid={`cell-${field}-${spaceId}`}
      className={`cursor-pointer px-1.5 py-0.5 rounded-sm transition-colors font-mono text-xs truncate border border-transparent hover:border-primary/40 hover:bg-orange-500/10 ${
        display === '--' ? 'text-muted-foreground' : ''
      }`}
      title={`Click to edit`}
    >
      {display}
    </span>
  );
}

function SpaceTypeDropdown({ value, spaceId, onSave }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const shortLabel = (v) => {
    if (!v) return '--';
    if (v.length > 22) return v.slice(0, 20) + '...';
    return v;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        data-testid={`space-type-btn-${spaceId}`}
        className={`flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-xs font-mono cursor-pointer transition-colors min-w-0 border ${
          value
            ? 'text-teal-400 border-transparent hover:border-teal-500/40 hover:bg-teal-500/10'
            : 'text-muted-foreground border-dashed border-border hover:border-primary/40 hover:bg-orange-500/10 hover:text-primary'
        }`}
        title={value || 'Click to set space type'}
      >
        <span>{shortLabel(value)}</span>
        <ChevronDown className="w-2.5 h-2.5 shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 mt-1 w-64 max-h-56 overflow-auto bg-card border border-border rounded-md shadow-xl shadow-black/50"
          data-testid={`space-type-dropdown-${spaceId}`}>
          <button
            onClick={(e) => { e.stopPropagation(); onSave(spaceId, { space_type: null }); setOpen(false); }}
            className="w-full text-left px-3 py-1.5 text-xs font-mono text-muted-foreground hover:bg-secondary/50 hover:text-foreground transition-colors"
          >
            -- None --
          </button>
          {SPACE_TYPES.map((st) => (
            <button
              key={st}
              onClick={(e) => { e.stopPropagation(); onSave(spaceId, { space_type: st }); setOpen(false); }}
              data-testid={`space-type-option-${st.replace(/[^a-zA-Z0-9]/g, '-')}`}
              className={`w-full text-left px-3 py-1.5 text-xs font-mono transition-colors ${
                st === value
                  ? 'bg-teal-950/40 text-teal-400'
                  : 'text-foreground hover:bg-secondary/50 hover:text-foreground'
              }`}
            >
              {st}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function EditableSpacesPanel({
  spaces = [], selectedSpaceId, onSpaceClick,
  onUpdateSpace, onCreateSpace, onDeleteSpace, onReview,
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [newRoom, setNewRoom] = useState('');
  const [newName, setNewName] = useState('');

  const handleAdd = () => {
    if (!newRoom.trim()) return;
    onCreateSpace({ room_number: newRoom.trim(), room_name: newName.trim() });
    setNewRoom('');
    setNewName('');
    setShowAdd(false);
  };

  return (
    <div data-testid="editable-spaces-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border/50">
        <span className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">
          Click any value to edit
        </span>
        <button
          onClick={() => setShowAdd(!showAdd)}
          data-testid="add-space-btn"
          className="flex items-center gap-1 px-2 py-1 text-[10px] font-heading uppercase tracking-wider text-emerald-400 border border-emerald-900/50 rounded-sm hover:bg-emerald-950/30 transition-colors"
        >
          <Plus className="w-3 h-3" /> Add Space
        </button>
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="px-4 py-3 border-b border-border/50 bg-emerald-950/10" data-testid="add-space-form">
          <div className="grid grid-cols-2 gap-2 mb-2">
            <input
              value={newRoom}
              onChange={(e) => setNewRoom(e.target.value)}
              placeholder="Room #"
              data-testid="new-space-room"
              className="bg-card border border-border rounded-sm px-2 py-1 font-mono text-xs text-foreground outline-none focus:border-emerald-500"
            />
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Name"
              data-testid="new-space-name"
              className="bg-card border border-border rounded-sm px-2 py-1 font-mono text-xs text-foreground outline-none focus:border-emerald-500"
            />
          </div>
          <div className="flex gap-1">
            <button onClick={handleAdd} data-testid="confirm-add-space"
              className="px-2 py-1 text-[10px] font-mono bg-emerald-950/30 text-emerald-400 border border-emerald-900 rounded hover:bg-emerald-950/50 transition-colors">
              Add
            </button>
            <button onClick={() => setShowAdd(false)}
              className="px-2 py-1 text-[10px] font-mono text-muted-foreground hover:text-foreground transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Space list */}
      <div className="divide-y divide-slate-800/30">
        {spaces.map((space) => (
          <div
            key={space.id}
            className={`px-4 py-3 transition-all duration-300 group ${
              selectedSpaceId === space.id
                ? 'bg-blue-950/40 border-l-2 border-l-blue-400 ring-1 ring-inset ring-blue-500/30 shadow-lg shadow-blue-900/20'
                : 'hover:bg-secondary/20 border-l-2 border-l-transparent'
            }`}
            data-testid={`review-space-${space.id}`}
            onClick={() => onSpaceClick(space)}
          >
            {/* Row 1: Room # + Name */}
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1 min-w-0 flex-1">
                <span className="text-[9px] text-muted-foreground shrink-0">Room:</span>
                <EditableCell value={space.room_number} field="room_number" spaceId={space.id}
                  onSave={onUpdateSpace} />
                <span className="text-[9px] text-muted-foreground shrink-0 ml-1">Name:</span>
                <EditableCell value={space.room_name} field="room_name" spaceId={space.id}
                  onSave={onUpdateSpace} />
              </div>
              <div className="flex items-center gap-1 shrink-0 ml-2">
                <button
                  onClick={(e) => { e.stopPropagation(); onReview && onReview('space', space.id, 'approved'); }}
                  data-testid={`review-approve-space-${space.id}`}
                  title="Approve space"
                  className={`p-1 rounded border transition-colors ${
                    space.status === 'approved'
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'text-muted-foreground border-transparent hover:text-emerald-500 hover:border-emerald-400 dark:hover:border-emerald-900'
                  }`}
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onReview && onReview('space', space.id, 'rejected'); }}
                  data-testid={`review-reject-space-${space.id}`}
                  title="Reject space"
                  className={`p-1 rounded border transition-colors ${
                    space.status === 'rejected'
                      ? 'bg-rose-600 text-white border-rose-600'
                      : 'text-muted-foreground border-transparent hover:text-rose-500 hover:border-rose-400 dark:hover:border-rose-900'
                  }`}
                >
                  <XCircle className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onDeleteSpace(space.id); }}
                  data-testid={`delete-space-${space.id}`}
                  className="p-1 text-muted-foreground hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-all"
                  title="Delete space"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>

            {/* Row 2: Space Type + SF inclusion */}
            <div className="flex items-center gap-1 mb-1">
              <span className="text-[9px] text-muted-foreground shrink-0">Type:</span>
              <SpaceTypeDropdown value={space.space_type} spaceId={space.id} onSave={onUpdateSpace} />
              {space.space_type && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const newVal = !(space.include_in_sf !== false);
                    onUpdateSpace(space.id, { include_in_sf: newVal });
                  }}
                  data-testid={`sf-toggle-${space.id}`}
                  className={`ml-1 px-1.5 py-0.5 rounded text-[8px] font-mono uppercase tracking-wider border transition-colors ${
                    space.include_in_sf === false
                      ? 'text-muted-foreground border-border bg-secondary/30 hover:border-emerald-700'
                      : 'text-emerald-400 border-emerald-800 bg-emerald-950/30 hover:border-emerald-600'
                  }`}
                  title={space.include_in_sf === false ? 'Not counted in building SF total' : 'Counted in building SF total'}
                >
                  {space.include_in_sf === false ? 'Excl SF' : 'Incl SF'}
                </button>
              )}
            </div>

            {/* Row 3: Area + Level + Department + Confidence */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-0.5">
                <span className="text-[9px] text-muted-foreground">Area:</span>
                <EditableCell value={space.area} field="area" spaceId={space.id}
                  onSave={onUpdateSpace} type="number" />
                <span className="text-[9px] text-muted-foreground">sf</span>
              </div>
              <div className="flex items-center gap-0.5">
                <span className="text-[9px] text-muted-foreground">Level:</span>
                <EditableCell value={space.level_key} field="level_key" spaceId={space.id}
                  onSave={onUpdateSpace} />
              </div>
              <div className="flex items-center gap-0.5">
                <span className="text-[9px] text-muted-foreground">Dept:</span>
                <EditableCell value={space.department} field="department" spaceId={space.id}
                  onSave={onUpdateSpace} />
              </div>
              {space.confidence != null && (
                <span className={`text-[9px] font-mono px-1 py-0.5 rounded border ${
                  space.confidence >= 0.9 ? 'text-emerald-400 border-emerald-900 bg-emerald-950/30'
                  : space.confidence >= 0.75 ? 'text-amber-400 border-amber-900 bg-amber-950/30'
                  : 'text-rose-400 border-rose-900 bg-rose-950/30'
                }`}>
                  {Math.round(space.confidence * 100)}%
                </span>
              )}
            </div>
          </div>
        ))}
        {spaces.length === 0 && (
          <div className="py-8 text-center text-muted-foreground text-xs">
            No spaces yet. Draw polygons or add manually.
          </div>
        )}
      </div>
    </div>
  );
}
